import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { gamoraApi, CollectionCountResponse, CollectionStatsResponse, StandardDocumentsStatsResponse, getRegulatoryBodyLabel } from '../services/api';

const CollectionManagement: React.FC = () => {
  const [collectionInfo, setCollectionInfo] = useState<CollectionCountResponse | null>(null);
  const [collectionStats, setCollectionStats] = useState<CollectionStatsResponse | null>(null);
  const [standardStats, setStandardStats] = useState<StandardDocumentsStatsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCollectionInfo = async () => {
    setLoading(true);
    setError(null);

    try {
      const [collectionResponse, standardsResponse] = await Promise.all([
        gamoraApi.getCollectionStats(),
        gamoraApi.getStandardDocumentsStats(),
      ]);
      setCollectionStats(collectionResponse);
      setCollectionInfo({
        collection_name: collectionResponse.collection_name,
        count: collectionResponse.total_count,
        status: collectionResponse.status,
      });
      setStandardStats(standardsResponse);
    } catch (err) {
      setError('Failed to fetch collection information');
      console.error('Collection info error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCollectionInfo();
  }, []);

  const getStatusText = (status: string) => {
    switch (status) {
      case 'has_data':
        return 'Has Data';
      case 'empty':
        return 'Empty';
      default:
        return 'Unknown';
    }
  };

  return (
    <Box sx={{ maxWidth: 700, mx: 'auto', p: { xs: 2, md: 3 } }}>
      <Typography variant="h5" component="h1" align="center" sx={{ fontSize: '1.1rem', fontWeight: 650, color: '#111827', letterSpacing: '-0.02em', mb: 1 }}>
        Collection Management
      </Typography>
      <Typography variant="body2" align="center" sx={{ fontSize: '0.8rem', color: '#9ca3af', mb: 3 }}>
        Monitor and manage your knowledge base collection
      </Typography>

      {/* Refresh Button */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
        <Button
          variant="outlined"
          onClick={fetchCollectionInfo}
          disabled={loading}
          startIcon={<RefreshIcon sx={{ color: '#6b7280' }} />}
          sx={{
            borderColor: '#e5e7eb',
            borderRadius: '10px',
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '0.78rem',
            color: '#6b7280',
            '&:hover': { backgroundColor: '#f3f4f6', borderColor: '#e5e7eb' },
            minWidth: 180,
          }}
        >
          {loading ? <CircularProgress size={18} sx={{ color: '#6b7280' }} /> : 'Refresh'}
        </Button>
      </Box>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: '10px' }}>
          {error}
        </Alert>
      )}

      {/* Collection Information */}
      {collectionInfo && (
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, flexWrap: 'wrap' }}>
          <Card sx={{ flex: 1, backgroundColor: '#fff', boxShadow: 'none', border: '1px solid #eceef1', borderRadius: '12px', p: 0 }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="subtitle1" sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#111827', mb: 1 }}>
                Collection Details
              </Typography>
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" sx={{ color: '#9ca3af', fontSize: '0.72rem' }}>
                  Collection Name
                </Typography>
                <Typography variant="body2" sx={{ mb: 1, color: '#111827', fontWeight: 400 }}>
                  {collectionInfo.collection_name}
                </Typography>
                <Typography variant="caption" sx={{ color: '#9ca3af', fontSize: '0.72rem' }}>
                  Status
                </Typography>
                <Typography variant="body2" sx={{ mb: 0, color: '#111827', fontWeight: 400 }}>
                  {getStatusText(collectionInfo.status)}
                </Typography>
              </Box>
            </CardContent>
          </Card>

          <Card sx={{ flex: 1, minWidth: 260, backgroundColor: '#fff', boxShadow: 'none', border: '1px solid #eceef1', borderRadius: '12px', p: 0 }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="subtitle1" sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#111827', mb: 1 }}>
                Statistics
              </Typography>
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" sx={{ color: '#9ca3af', fontSize: '0.72rem' }}>
                  Total Records
                </Typography>
                <Typography variant="h6" sx={{ mb: 1, color: '#111827', fontWeight: 600, fontSize: 22 }}>
                  {collectionInfo.count.toLocaleString()}
                </Typography>
                <Typography variant="caption" sx={{ color: '#9ca3af', fontSize: '0.72rem' }}>
                  Collection Status
                </Typography>
                <Typography variant="body2" sx={{ color: '#111827', fontWeight: 400 }}>
                  {collectionInfo.count === 0 
                    ? 'No documents embedded yet. Use the Embedding tab to add content.'
                    : `Ready for search with ${collectionInfo.count} embedded chunks.`
                  }
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {collectionStats && collectionStats.by_country.length > 0 && (
            <Card sx={{ flex: '1 1 100%', backgroundColor: '#fff', boxShadow: 'none', border: '1px solid #eceef1', borderRadius: '12px', p: 0 }}>
              <CardContent sx={{ p: 2 }}>
                <Typography variant="subtitle1" sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#111827', mb: 1 }}>
                  By Country / Regulatory Authority
                </Typography>
                <TableContainer sx={{ maxHeight: 360, border: '1px solid #eceef1', borderRadius: '10px' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ backgroundColor: '#f8f9fb', color: '#6b7280', fontWeight: 600, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                          Country
                        </TableCell>
                        <TableCell sx={{ backgroundColor: '#f8f9fb', color: '#6b7280', fontWeight: 600, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                          Regulatory Authority
                        </TableCell>
                        <TableCell sx={{ backgroundColor: '#f8f9fb', color: '#6b7280', fontWeight: 600, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em' }} align="right">
                          Chunks
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {collectionStats.by_country.map((item) => (
                        <TableRow key={item.key} hover sx={{ '&:hover': { backgroundColor: '#f8f9fb' } }}>
                          <TableCell sx={{ fontSize: '0.82rem', color: '#374151', borderBottom: '1px solid #f3f4f6' }}>{item.key}</TableCell>
                          <TableCell sx={{ fontSize: '0.82rem', color: '#374151', borderBottom: '1px solid #f3f4f6' }}>
                            {getRegulatoryBodyLabel(item.key)}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.82rem', color: '#111827', borderBottom: '1px solid #f3f4f6' }} align="right">
                            {item.count.toLocaleString()}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}

          {collectionStats && collectionStats.by_jurisdiction.length > 0 && (
            <Card sx={{ flex: 1, minWidth: 280, backgroundColor: '#fff', boxShadow: 'none', border: '1px solid #eceef1', borderRadius: '12px', p: 0 }}>
              <CardContent sx={{ p: 2 }}>
                <Typography variant="subtitle1" sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#111827', mb: 1 }}>
                  By Jurisdiction
                </Typography>
                <TableContainer sx={{ maxHeight: 300, border: '1px solid #eceef1', borderRadius: '10px' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ backgroundColor: '#f8f9fb', color: '#6b7280', fontWeight: 600, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                          Jurisdiction
                        </TableCell>
                        <TableCell sx={{ backgroundColor: '#f8f9fb', color: '#6b7280', fontWeight: 600, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em' }} align="right">
                          Chunks
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {collectionStats.by_jurisdiction.map((item) => (
                        <TableRow key={item.key} hover sx={{ '&:hover': { backgroundColor: '#f8f9fb' } }}>
                          <TableCell sx={{ fontSize: '0.82rem', color: '#374151', borderBottom: '1px solid #f3f4f6' }}>{item.key}</TableCell>
                          <TableCell sx={{ fontSize: '0.82rem', color: '#111827', borderBottom: '1px solid #f3f4f6' }} align="right">
                            {item.count.toLocaleString()}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}

          {collectionStats && collectionStats.by_source_type.length > 0 && (
            <Card sx={{ flex: 1, minWidth: 280, backgroundColor: '#fff', boxShadow: 'none', border: '1px solid #eceef1', borderRadius: '12px', p: 0 }}>
              <CardContent sx={{ p: 2 }}>
                <Typography variant="subtitle1" sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#111827', mb: 1 }}>
                  By Source Type
                </Typography>
                <TableContainer sx={{ maxHeight: 300, border: '1px solid #eceef1', borderRadius: '10px' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ backgroundColor: '#f8f9fb', color: '#6b7280', fontWeight: 600, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                          Source Type
                        </TableCell>
                        <TableCell sx={{ backgroundColor: '#f8f9fb', color: '#6b7280', fontWeight: 600, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em' }} align="right">
                          Chunks
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {collectionStats.by_source_type.map((item) => (
                        <TableRow key={item.key} hover sx={{ '&:hover': { backgroundColor: '#f8f9fb' } }}>
                          <TableCell sx={{ fontSize: '0.82rem', color: '#374151', borderBottom: '1px solid #f3f4f6' }}>{item.key}</TableCell>
                          <TableCell sx={{ fontSize: '0.82rem', color: '#111827', borderBottom: '1px solid #f3f4f6' }} align="right">
                            {item.count.toLocaleString()}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}
          {standardStats && (
            <Card sx={{ flex: 1, minWidth: 280, backgroundColor: '#fff', boxShadow: 'none', border: '1px solid #eceef1', borderRadius: '12px', p: 0 }}>
              <CardContent sx={{ p: 2 }}>
                <Typography variant="subtitle1" sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#111827', mb: 1 }}>
                  Standards (Postgres)
                </Typography>
                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" component="span" sx={{ color: '#9ca3af', fontSize: '0.72rem' }}>Total: </Typography>
                  <Typography variant="body2" component="span" sx={{ color: '#111827', fontSize: '0.82rem' }}>{standardStats.total_count.toLocaleString()}</Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {standardStats.by_country.length > 0 && (
                    <TableContainer sx={{ maxHeight: 220, border: '1px solid #eceef1', borderRadius: '10px' }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ backgroundColor: '#f8f9fb', color: '#6b7280', fontWeight: 600, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                              Country
                            </TableCell>
                            <TableCell sx={{ backgroundColor: '#f8f9fb', color: '#6b7280', fontWeight: 600, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em' }} align="right">
                              Docs
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {standardStats.by_country.map((item) => (
                            <TableRow key={`std-country-${item.key}`} hover sx={{ '&:hover': { backgroundColor: '#f8f9fb' } }}>
                              <TableCell sx={{ fontSize: '0.82rem', color: '#374151', borderBottom: '1px solid #f3f4f6' }}>{item.key}</TableCell>
                              <TableCell sx={{ fontSize: '0.82rem', color: '#111827', borderBottom: '1px solid #f3f4f6' }} align="right">
                                {item.count.toLocaleString()}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                  {standardStats.by_regulatory_authority.length > 0 && (
                    <TableContainer sx={{ maxHeight: 220, border: '1px solid #eceef1', borderRadius: '10px' }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ backgroundColor: '#f8f9fb', color: '#6b7280', fontWeight: 600, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                              Regulatory Authority
                            </TableCell>
                            <TableCell sx={{ backgroundColor: '#f8f9fb', color: '#6b7280', fontWeight: 600, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em' }} align="right">
                              Docs
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {standardStats.by_regulatory_authority.map((item) => (
                            <TableRow key={`std-ra-${item.key}`} hover sx={{ '&:hover': { backgroundColor: '#f8f9fb' } }}>
                              <TableCell sx={{ fontSize: '0.82rem', color: '#374151', borderBottom: '1px solid #f3f4f6' }}>{item.key}</TableCell>
                              <TableCell sx={{ fontSize: '0.82rem', color: '#111827', borderBottom: '1px solid #f3f4f6' }} align="right">
                                {item.count.toLocaleString()}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                  {standardStats.by_status.length > 0 && (
                    <TableContainer sx={{ maxHeight: 200, border: '1px solid #eceef1', borderRadius: '10px' }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ backgroundColor: '#f8f9fb', color: '#6b7280', fontWeight: 600, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                              Status
                            </TableCell>
                            <TableCell sx={{ backgroundColor: '#f8f9fb', color: '#6b7280', fontWeight: 600, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em' }} align="right">
                              Docs
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {standardStats.by_status.map((item) => (
                            <TableRow key={`std-status-${item.key}`} hover sx={{ '&:hover': { backgroundColor: '#f8f9fb' } }}>
                              <TableCell sx={{ fontSize: '0.82rem', color: '#374151', borderBottom: '1px solid #f3f4f6' }}>{item.key}</TableCell>
                              <TableCell sx={{ fontSize: '0.82rem', color: '#111827', borderBottom: '1px solid #f3f4f6' }} align="right">
                                {item.count.toLocaleString()}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Box>
              </CardContent>
            </Card>
          )}
        </Box>
      )}

      {/* Empty State */}
      {!collectionInfo && !loading && !error && (
        <Paper sx={{ p: 3, textAlign: 'center', backgroundColor: '#fff', boxShadow: 'none', border: '1px solid #eceef1', borderRadius: '12px' }}>
          <Typography variant="subtitle1" sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#111827', mb: 1 }}>
            No Collection Information
          </Typography>
          <Typography variant="body2" sx={{ color: '#9ca3af', fontSize: '0.72rem', fontWeight: 400 }}>
            Click "Refresh" to load collection details.
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default CollectionManagement; 